<template lang="pug">
modal.width-max(:title='$t("addResource.savedTitle")' @closeModal='hideModal')
  .column
    p.subtitle.text-normal.dark-gray.m4-top-4
      | {{ $t('addResource.subTitle') }}
    .folders-container
      .tab-selection.width-100
        button.tab-selector.center.text-normal(type='button' @click='changeTab(true)' :class='{ active: isOwnerTab }')
          | {{ $t('folders.tabs.myFolders') }}
        button.tab-selector.center.text-normal(type='button' @click='changeTab(false)' :class='{ active: !isOwnerTab }')
          | {{ $t('folders.tabs.sharedWithMe') }}
      #folders.folders(v-if='showFolders')
        checkbox-list.m4-top-6(
          name='filteredList'
          :options='activeFolderList'
          keyName='folderId'
          labelName='folderName'
          :value='filteredList'
          :hasCustomUpdate='true'
          :customIsChecked='isChecked'
          :customIsDisabled='isDisabled'
          :hoverCheckbox='true'
          @callback='toggleFolder'
          :disabledText='$t("addResource.noEditPermissions")'
        )
        transition(name='fade')
          form.column.folders.new-folder.middle.simple-svg-wrapper(
            @submit.prevent='addNewFolder'
            novalidate
            v-show='newFolder'
          )
            input.row.middle.input.width-100(
              :aria-label='$t("addResource.folderName")'
              type='text'
              :placeholder='$t("addResource.inputPlaceholder")'
              v-model='folderName'
              @keydown.esc='closeNewFolder'
              :class='{ error: errorDetected }'
            )
            .add-folder-buttons.row.end.m4-top-2
              button.link-button-blue.m4-right-5(type='button' @click='closeNewFolder')
                | {{ $t('addResource.cancel') }}
              button.create-folder-button.add-icon.bold(
                type='submit'
                :class='{ disabled: !folderLength }'
                :disabled='!folderLength'
              )
                | {{ $t('addResource.createFolder') }}
      .no-folders.column.middle.center(v-else)
        simple-svg.dark-gray-icon.m4-right-2.m4-bottom-5(:filepath='addFolder' :width='"25px"' :height='"25px"')
        p.text-normal.dark-gray.text-center
          | {{ $t('addResource.noFolders') }}
    .error-container.m4-bottom-5(v-if='showError || invalidName')
      span.error-desc(v-if='invalidName')
        | {{ $t('addResource.invalidNameFolder') }}
      span.error-desc(v-if='!$v.folderName.maxLength')
        | {{ $t('addResource.maxLengthError') }}
    .row.end
      button.text-normal.bold.blue.with-icon.row.middle(v-show='isOwnerTab' @click='createNewFolder' :disabled='!isOwnerTab')
        simple-svg.blue-icon.m4-right-2.m-bottom-05(
          :filepath='plusFolderIcon'
          :width='"24px"'
          :height='"24px"'
        )
        | {{ $t('addResource.addFolder') }}
      button.primary-button.save-button(
        type='button'
        @click='handleSubmit'
        :disabled='!hasChange'
      )
        | {{ $t('addResource.save') }}
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { maxLength } from 'vuelidate/lib/validators'
import amplitude from 'amplitude-js'

import CheckboxList from '@/components/Inputs/CheckboxList'

import Modal from '../Modal'

import CustomToast from '@/components/CustomToast'

import addIcon from '@/assets/ic_circle-add.svg'
import addFolder from '@/assets/ic_new-folder.svg'
import folderIcon from '@/assets/ic_folder.svg'
import checkedFolderIcon from '@/assets/ic_folder-check.svg'
import plusFolderIcon from '@/assets/ic_folder-plus.svg'
import checkIcon from '@/assets/ic_check_base.svg'

import { routes } from '../../router'
import { userPermissions } from '../../utils/constants'
import { i18n } from '../../main'

export default {
  components: { Modal, CheckboxList, CustomToast },
  data () {
    return {
      folderIcon,
      checkedFolderIcon,
      plusFolderIcon,
      addFolder,
      addIcon,
      checkIcon,
      saved: true,
      newFolder: false,
      folderName: '',
      showError: false,
      hasChange: false,
      isOwnerTab: true
    }
  },
  computed: {
    ...mapState({
      invalidName: state => state.folders.invalidNameFolder,
      props: state => state.behavior.addResourceModal.props,
      ownedFolders: state => state.folders.resourceFoldersOwned,
      sharedFolders: state => state.folders.resourceFoldersShared,
      ownedFoldersEdition: state => state.folders.resourceFoldersOwnedEdition,
      sharedFoldersEdition: state => state.folders.resourceFoldersSharedEdition,
      categories: state => state.dashboard.categories,
      innerLocale: state => state.behavior.innerLocale,
      locale: state => state.behavior.locale
    }),
    errorDetected() {
      return this.invalidName || this.showError
    },
    showFolders() {
      return (this.isOwnerTab && this.ownedFolders.length) || (!this.isOwnerTab && this.sharedFolders) || this.newFolder
    },
    activeFolderList() {
      return this.isOwnerTab ? this.ownedFolders : this.sharedFolders
    },
    filteredList() {
      return this.activeFolderList.filter(folder => folder.folderId)
    },
    folderLength() {
      return this.folderName.length
    }
  },
  mounted() {
    this.hydrateResourceFolders({
      id: this.props.id,
      locale: this.props.locale || this.innerLocale
    })
  },
  beforeDestroy() {
    this.clearResourceFolders()
  },
  updated() {
    this.$nextTick(() => {
      this.$refs.folderName && this.$refs.folderName.focus()
    })
  },
  validations: {
    folderName: { maxLength: maxLength(50) }
  },
  methods: {
    ...mapActions([
      'createAndToggle',
      'hydrateResourceFolders',
      'clearResourceFolders',
      'getFolderDetails',
      'hideElement',
      'addToFolders',
      'toggleResourceFolders',
      'clearError'
    ]),
    toggleFolder(name, key) {
      const data = {
        folderId: key,
        isOwner: this.isOwnerTab
      }
      this.hasChange = true
      this.toggleResourceFolders(data)
    },
    setInFolder(field, value) {
      this[field] = value
      this.hasChange = true
    },
    changeTab(isOwnerTab) {
      if (!isOwnerTab) this.closeNewFolder()
      this.isOwnerTab = isOwnerTab
    },
    async handleSubmit() {
      await this.addToFolders({
        ownedFolders: this.ownedFoldersEdition,
        sharedFolders: this.sharedFoldersEdition,
        resource: this.props.id,
        locale: this.props.locale || this.innerLocale
      })

      const resource = this.props.resource
      const resourceCategory = this.categories.find(category => category.id === resource.categoryId)
      amplitude.getInstance().logEvent('Add Resource To Folder', {
        name: resource.name,
        id: this.props.id,
        educationalProgram: resource.educationalProgram ? resource.educationalProgram.name : '',
        category: (resourceCategory && resourceCategory.name) || ''
      })
      if (this.$route.name === routes.folderContent) {
        await this.getFolderDetails(this.$route.params.id)
      }
      this.hideElement({ element: 'addResourceModal' })
      this.$notify({
        group: 'custom-toast',
        text: i18n.t('customToast.addResourceToFolderSuccess'),
        type: 'success'
      })
    },
    createNewFolder() {
      this.newFolder = !this.newFolder
      if (!this.newFolder) {
        this.folderName = ''
        this.clearError(false)
        this.showError = false
      }
      setTimeout(() => {
        document
          .getElementById('folders')
          .scrollIntoView({ block: 'end', behavior: 'smooth' })
      }, 200)
    },
    closeNewFolder() {
      this.newFolder = false
      this.folderName = ''
    },
    async addNewFolder() {
      if (this.$v.$invalid) {
        this.showError = true
      } else {
        this.showError = false
        await this.createAndToggle({
          folder: this.folderName,
          resource: this.props.id
        })
        if (this.invalidName) this.clearError(false)
        this.folderName = ''
        this.hasChange = true
      }
      this.hydrateResourceFolders({ id: this.props.id, locale: this.locale })
    },
    hideModal() {
      this.folderName = ''
      this.clearError(false)
      this.showError = false
      this.activeFolderList = []
      this.hideElement({ element: 'addResourceModal' })
    },
    isChecked(key) {
      return this.activeFolderList.find(folder => folder.folderId === key).inFolder
    },
    isDisabled(key) {
      const { permissionName } = this.activeFolderList.find(folder => folder.folderId === key)
      return permissionName === userPermissions.read
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';

.width-max {
  --max-width: 550px;
}

.new-folder {
  margin-top: 10px;
  min-height: 88px;
}

.input {
  border: 1px solid $gray;
  border-radius: 4px;
  font-size: 16px;
  height: 40px;
  margin: 5px;
  padding-left: 15px;

  &:focus {
    border: 1px solid $blue
  }

  &.error {
    border: 1px solid $red;
  }
}

::-webkit-scrollbar {
  width: 10px;
}

::-webkit-scrollbar-track {
  background: $white;
}

::-webkit-scrollbar-thumb {
  background: $gray;
  border-radius: 5px;
  height: 72px;
}

.label {
  align-items: center;
  cursor: pointer;
  font-size: 16px;
  height: 40px;
  margin-right: 10px;
  position: relative;
}

.toggle-button {
  background-image: url('../../assets/ic_circle-add.svg');
  background-size: cover;
  height: 22px;
  width: 22px;

  &.active {
    background-image: url('../../assets/ic_check-incircle.svg');

    &:hover {
      background-image: url('../../assets/ic_circle_cross_red.svg');
    }
  }
}

.tab-selector {
  height: 50px;
  max-width: 226px;
  width: 100%;

  &:hover {
    color: $blue;
    font-weight: bold;
  }

  &.active {
    border-bottom: 5px solid $blue;
    color: $blue;
    font-weight: bold;
    z-index: 1;
  }
}

.tab-selection {
  align-items: center;
  display: flex;
  justify-content: space-between;
  max-width: 480px;
  position: relative;

  &::after {
    background-color: $light-gray;
    bottom: 0;
    content: '';
    height: 3px;
    position: absolute;
    width: 100%;
  }
}

.no-folders{
  margin: auto;
  max-width: 260px;
}

.title {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 30px;
  margin-top: 15px;
}

.create-folder-button {
  border: 1px solid $light-gray;
  border-radius: 4px;
  color: $white;
  max-height: 35px;
  min-width: 72px;
  width: 100%;
}

.folder {
  margin: 10px 0;
  padding: 12px;
}

.save.primary-button {
  --max-width: 160px;
  display: flex;
  justify-content: flex-start;
  margin-right: auto;
  padding-left: 0;
}

.empty {
  margin: 20px 0;
}

.folders-permission-selector {
  justify-content: space-around;
  width: 100%;
}

.folders-container {
  border-bottom: 1px solid $light-gray;
  border-top: 1px solid $light-gray;
  display: flex;
  flex-direction: column;
  height: 300px;
  justify-content: flex-start;
  margin: 20px 0 30px;
  overflow-x: hidden;
  overflow-y: scroll;
  position: relative;

  &:before,
  &:after {
    content: '';
    left: -45px;
    position: absolute;
    width: 495px;
  }
}

.subtitle {
  font-size: 20px;
  width: 100%;
}

.radios-container {
  margin: 25px 0;
}

.add-icon {
  align-items: center;
  background: $blue;
  border-bottom-right-radius: 2px;
  border-top-right-radius: 2px;
  display: flex;
  height: 40px;
  justify-content: center;
  max-width: 40px;
  width: 100%;
}

.new-folder-input:focus {
  border: 1px solid $blue
}

.save-button {
  margin-left: auto;
}

.disabled {
  background: $light-gray;
  color: $dark-gray;
  cursor: not-allowed;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .5s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

</style>
